// Consent the HOST page's own cookie banner gave: read where the widget is
// embedded in a venue's website (lib/embed-context.ts), for the checkout's
// marketing box (state/tracking.ts consentWasInherited).
//
// consentModeGranted is a COPY of the dataLayer half of
// inheritedConsentGranted in src/tag.ts (the site tag), kept in step with it
// by hand: tag.ts is a separate, dependency-free bundle, so it cannot import
// this file, and this bundle must not pull in tag.ts. Change one, change both.

/**
 * Google Consent Mode v2 state in a dataLayer: every mainstream CMP
 * (Cookiebot, CookieYes, OneTrust, Complianz…) pushes its consent default and
 * updates there. The LAST consent entry wins; ad_storage or analytics_storage
 * granted counts.
 */
export function consentModeGranted(dl: unknown): boolean {
  if (!Array.isArray(dl)) return false;
  let granted = false;
  for (const entry of dl) {
    // gtag('consent', 'default'|'update', {...}) lands as an Arguments-like
    // array: ["consent", "update", {ad_storage: "granted", ...}].
    const e = entry as { [i: number]: unknown; length?: number };
    if (!e || e[0] !== "consent") continue;
    const state = e[2] as Record<string, string> | undefined;
    if (!state) continue;
    granted =
      state.ad_storage === "granted" || state.analytics_storage === "granted";
  }
  return granted;
}

/** The banner's Consent Mode settings that go with its consent state. */
const CONSENT_SETTINGS = ["ads_data_redaction", "url_passthrough"] as const;

/**
 * The Consent Mode commands in a dataLayer from index `from` on, in order,
 * each as its arguments: what tracking.ts copies into Vertu's own data layer.
 *   ["consent", "default"|"update", {...}, ...]   as they are;
 *   ["set", "ads_data_redaction"|"url_passthrough", value]   as they are;
 *   ["set", {...}]   only those two keys, never any other setting the page
 *                    keeps for its own tags.
 * Google keeps consent state for the whole page once a Google tag has read
 * these, but a page that only has its banner's gtag stub has no tag of its own
 * to read them, and Vertu's tag reads only its own data layer.
 */
export function consentCommands(dl: unknown, from = 0): unknown[][] {
  if (!Array.isArray(dl)) return [];
  const out: unknown[][] = [];
  for (let i = Math.max(0, from); i < dl.length; i++) {
    const e = dl[i] as { [i: number]: unknown; length?: number } | null;
    if (!e || typeof e !== "object") continue;
    const length = typeof e.length === "number" ? e.length : 0;
    if (e[0] === "consent") {
      if (e[1] !== "default" && e[1] !== "update") continue;
      out.push(Array.from({ length }, (_, k) => e[k]));
    } else if (e[0] === "set") {
      if (typeof e[1] === "string") {
        if ((CONSENT_SETTINGS as readonly string[]).includes(e[1])) out.push(["set", e[1], e[2]]);
      } else if (e[1] && typeof e[1] === "object") {
        const all = e[1] as Record<string, unknown>;
        const kept = Object.fromEntries(CONSENT_SETTINGS.filter((k) => k in all).map((k) => [k, all[k]]));
        if (Object.keys(kept).length > 0) out.push(["set", kept]);
      }
    }
  }
  return out;
}

/**
 * The widget's mount node for this venue carries data-consent="granted": the
 * site owner asserts their banner covers the booking widget (their CMP can
 * also set it later). Read like main.tsx reads the mount: data-tenant, else
 * the page's ?tenant=.
 */
export function mountNodeConsentGranted(tenantSlug: string): boolean {
  if (typeof document === "undefined" || !tenantSlug) return false;
  const pageTenant = (() => {
    try {
      return new URLSearchParams(window.location.search).get("tenant")?.trim() || "";
    } catch {
      return "";
    }
  })();
  const nodes = document.querySelectorAll<HTMLElement>(
    '[id="vertu-widget"], [data-vertu-widget]',
  );
  for (const node of Array.from(nodes)) {
    if ((node.dataset.tenant || pageTenant) !== tenantSlug) continue;
    if (node.dataset.consent === "granted") return true;
  }
  return false;
}

/**
 * Has the host page granted consent for this venue's widget, as of NOW? Read
 * every time it is asked, never cached: a banner answers after the page (and
 * the widget) loaded.
 */
export function hostConsentGranted(tenantSlug: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    return (
      consentModeGranted((window as { dataLayer?: unknown }).dataLayer) ||
      mountNodeConsentGranted(tenantSlug)
    );
  } catch {
    return false;
  }
}
